import { Alert, MenuItem, Stack, TextField } from "@mui/material"
import type { BiomeshUnitResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  getListUnitsBiomeshUnitListGetQueryKey,
  useCreateUnitBiomeshUnitPost,
  useListContactsBiomeshContactListGet,
  useListOperationsBiomeshOperationListGet,
  useUpdateUnitBiomeshUnitUnitIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { Modal } from "../Shared/Modal"
import { ContactFormDialog } from "./ContactFormDialog"
import {
  branchTypeOptions,
  extractApiError,
  isBranchType,
  isUnitType,
  unitTypeOptions,
} from "./copUtils"
import { MultiAutocomplete } from "./MultiAutocomplete"

interface UnitFormDialogProps {
  open: boolean
  onClose: () => void
  unit?: BiomeshUnitResponse
  onSaved?: (unitId: string) => void
}

export const UnitFormDialog: FC<UnitFormDialogProps> = ({
  open,
  onClose,
  unit,
  onSaved,
}) => {
  const isEdit = !!unit
  const queryClient = useQueryClient()

  const [name, setName] = useState(unit?.name ?? "")
  const [unitType, setUnitType] = useState(unit?.unit_type ?? "")
  const [branchType, setBranchType] = useState(unit?.branch_type ?? "")
  const [uic, setUic] = useState(unit?.uic ?? "")
  const [operationIds, setOperationIds] = useState<string[]>(
    () =>
      unit?.operations?.flatMap(o =>
        o.operation_id ? [o.operation_id] : []
      ) ?? []
  )
  const [contactIds, setContactIds] = useState<string[]>(
    () =>
      unit?.contacts?.flatMap(c => (c.contact_id ? [c.contact_id] : [])) ?? []
  )
  const [formError, setFormError] = useState("")
  const [contactDialogOpen, setContactDialogOpen] = useState(false)

  const { data: operations } = useListOperationsBiomeshOperationListGet()
  const { data: contacts } = useListContactsBiomeshContactListGet()

  const { mutateAsync: createUnit, isPending: createPending } =
    useCreateUnitBiomeshUnitPost()
  const { mutateAsync: updateUnit, isPending: updatePending } =
    useUpdateUnitBiomeshUnitUnitIdPut()

  const isPending = createPending || updatePending

  const handleSave = useCallback(async () => {
    setFormError("")

    if (!name.trim()) {
      setFormError("Name is required")
      return
    }
    if (!unitType) {
      setFormError("Unit type is required")
      return
    }
    if (!branchType) {
      setFormError("Branch type is required")
      return
    }
    if (!uic.trim()) {
      setFormError("UIC is required")
      return
    }
    if (!isUnitType(unitType)) {
      setFormError("Invalid unit type")
      return
    }
    if (!isBranchType(branchType)) {
      setFormError("Invalid branch type")
      return
    }

    const payload = {
      name: name.trim(),
      unit_type: unitType,
      branch_type: branchType,
      uic: uic.trim(),
      operation_ids: operationIds,
      contact_ids: contactIds,
    }

    try {
      if (unit) {
        await updateUnit({ unitId: unit.unit_id, data: payload })
        enqueueSnackbar("Unit updated", { variant: "success" })
        onSaved?.(unit.unit_id)
      } else {
        const result = await createUnit({ data: payload })
        enqueueSnackbar("Unit created", { variant: "success" })
        onSaved?.(result.unit_id)
      }
      await queryClient.invalidateQueries({
        queryKey: getListUnitsBiomeshUnitListGetQueryKey(),
      })
      onClose()
    } catch (err) {
      setFormError(extractApiError(err, "Failed to save unit"))
    }
  }, [
    name,
    unitType,
    branchType,
    uic,
    operationIds,
    contactIds,
    unit,
    createUnit,
    updateUnit,
    queryClient,
    onClose,
    onSaved,
  ])

  const operationOptions =
    operations?.map(o => ({ id: o.operation_id, label: o.name })) ?? []
  const contactOptions =
    contacts?.map(c => ({
      id: c.contact_id,
      label: `${c.rank ? `${c.rank} ` : ""}${c.first_name} ${c.last_name}`,
    })) ?? []

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        onConfirm={() => void handleSave()}
        title={isEdit ? "Edit Unit" : "New Unit"}
        confirmLabel="Save"
        disableBtns={isPending}
      >
        <Stack spacing={1.5}>
          <TextField
            label="Name"
            required
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
          />

          <TextField
            label="Unit Type"
            select
            required
            fullWidth
            value={unitType}
            onChange={e => setUnitType(e.target.value)}
          >
            {unitTypeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Branch Type"
            select
            required
            fullWidth
            value={branchType}
            onChange={e => setBranchType(e.target.value)}
          >
            {branchTypeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="UIC"
            required
            fullWidth
            value={uic}
            onChange={e => setUic(e.target.value)}
          />

          <MultiAutocomplete
            label="Operations"
            options={operationOptions}
            selectedIds={operationIds}
            onChange={setOperationIds}
          />

          <MultiAutocomplete
            label="Contacts"
            options={contactOptions}
            selectedIds={contactIds}
            onChange={setContactIds}
            onCreateNew={() => setContactDialogOpen(true)}
          />

          {formError && <Alert severity="error">{formError}</Alert>}
        </Stack>
      </Modal>

      <ContactFormDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        onSaved={id => setContactIds(prev => [...prev, id])}
      />
    </>
  )
}
